import { HammerUIProvider } from '@liveauctioneers/hammer-ui-core/providers';
import { type ComponentProps, useMemo } from 'react';

import type { FeatureFlags } from '@/types/FeatureFlags';

import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import { useAmplitudeExperiments } from '@/utils/amplitude/experiments/utils/useAmplitudeExperiments';
import { mergeFeatureFlags } from '@/utils/amplitude/experiments/utils/variantsToFeatureFlags';

type Props = Omit<ComponentProps<typeof HammerUIProvider>, 'featureFlags' | 'theme'> & {
    /**
     * Flags from the feature-flag API; the Amplitude variants in context are layered on top.
     */
    baseFeatureFlags: FeatureFlags;
};

/**
 * The app's only HammerUIProvider, rendered by both the server and the client entry so the two
 * trees match on hydration. It sits inside the Amplitude provider because its feature flags come
 * from the experiment variants. A second HammerUIProvider anywhere in the tree renders a second
 * set of portal targets and providers that the server HTML does not have.
 */
export const AppHammerUIProvider = ({ baseFeatureFlags, children, ...rest }: Props) => {
    // featureFlagCookies: the dev-tools overrides, which win over everything else
    const { featureFlagCookies, variants } = useAmplitudeExperiments();
    const featureFlags = useMemo(
        () => mergeFeatureFlags(baseFeatureFlags, variants, featureFlagCookies),
        [baseFeatureFlags, featureFlagCookies, variants]
    );

    return (
        <HammerUIProvider
            {...rest}
            featureFlags={featureFlags}
            theme={mergedCaterwaulAndHammerUIThemes}
        >
            {children}
        </HammerUIProvider>
    );
};
